// Theme provider with persistent light/dark/system mode.
//
// We avoid `next-themes` (already a dep but heavier than needed) and roll a
// tiny provider that sets `data-theme` + the `dark` class on <html> so both
// Tailwind's `dark:` variant and our CSS variables react instantly.
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

type ThemeMode = "light" | "dark" | "system";
type ResolvedTheme = "light" | "dark";

type ThemeCtx = {
  theme: ThemeMode;
  resolved: ResolvedTheme;
  setTheme: (t: ThemeMode) => void;
};

const STORAGE_KEY = "hl_theme";
const Ctx = createContext<ThemeCtx | undefined>(undefined);

const getSystem = (): ResolvedTheme => {
  if (typeof window === "undefined") return "dark";
  return window.matchMedia("(prefers-color-scheme: light)").matches ? "light" : "dark";
};

const readStored = (): ThemeMode => {
  if (typeof window === "undefined") return "dark";
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    if (v === "light" || v === "dark" || v === "system") return v;
  } catch {
    /* ignore */
  }
  return "dark";
};

const apply = (resolved: ResolvedTheme) => {
  const root = document.documentElement;
  root.classList.toggle("dark", resolved === "dark");
  root.classList.toggle("light", resolved === "light");
  root.setAttribute("data-theme", resolved);
  root.style.colorScheme = resolved;
};

export const ThemeProvider = ({ children }: { children: ReactNode }) => {
  const [theme, setThemeState] = useState<ThemeMode>(() => readStored());
  const [resolved, setResolved] = useState<ResolvedTheme>(() =>
    readStored() === "system" ? getSystem() : (readStored() as ResolvedTheme),
  );

  useEffect(() => {
    const next = theme === "system" ? getSystem() : theme;
    setResolved(next);
    apply(next);
  }, [theme]);

  // React to OS-level changes when the user picked "system".
  useEffect(() => {
    if (theme !== "system") return;
    const mq = window.matchMedia("(prefers-color-scheme: light)");
    const onChange = () => {
      const next: ResolvedTheme = mq.matches ? "light" : "dark";
      setResolved(next);
      apply(next);
    };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [theme]);

  const setTheme = (t: ThemeMode) => {
    setThemeState(t);
    try {
      window.localStorage.setItem(STORAGE_KEY, t);
    } catch {
      /* ignore */
    }
  };

  return <Ctx.Provider value={{ theme, resolved, setTheme }}>{children}</Ctx.Provider>;
};

export const useTheme = (): ThemeCtx => {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
  return ctx;
};

// No-flash boot script — call before React mounts to avoid a dark→light flash.
export const initThemeEarly = () => {
  if (typeof window === "undefined") return;
  const stored = readStored();
  const resolved: ResolvedTheme = stored === "system" ? getSystem() : stored;
  apply(resolved);
};
